// Bigger Leaner Stronger progression (4th edition, 2022). Pure, no side effects.
// Page numbers are the printed book pages.
//
// The rule (p.255, 327-329):
//  - ONE top-rep set (6 on a 4-6 lift, 8 on a 6-8 lift) WITH reps left earns the jump.
//    Big compound lifts need at least 1 good rep left; accessory lifts can end 0-1 shy,
//    so any top-rep set counts. Six reps at true failure on a big lift = not ready.
//  - Add the weight right away: the NEXT set of the same workout. If that was the last
//    set, the first set next time starts at the new weight.
//  - At the new weight, hitting at least the bottom of the range locks it in.
//  - Missing the bottom: (1) take 5 lb off; (2) still under, go back to the old weight
//    and wait for 2 top-rep sets in one workout; (3) still under, wait for 3.
//    Still stuck after that, it's diet or sleep.
//  - Sharp pain ends the set: no jump, swap the lift (p.259).
//
// progressSets(opts) walks one exercise's logged work sets in order:
//   opts: { repLow, repHigh, increment, isPrimary, required = 1, startWeight,
//           carryFrom = null, painFlag, sets: [{ weight, reps, rir, isWarmup }] }
//   carryFrom: the weight before a jump earned on the LAST set of the previous workout
//              (so a miss on this workout's first set still gets the 5 lb shave).
//   -> { nextWeight, required, carryFrom, last: { action, message } | null }
// action: 'bump' | 'top' | 'no_rir' | 'shave' | 'revert' | 'locked' | 'hold' | 'under' | 'pain'

export const SHAVE_LB = 5;
export const MAX_REQUIRED = 3;

// Big lifts need 1+ good reps left; accessory lifts count at 0-1 left (p.257, 329).
export function rirCounts({ isPrimary, rir }) {
  if (!isPrimary) return true;
  return Number.isFinite(rir) && rir >= 1;
}

// Does this set need the "reps left?" question before it can count?
export function needsRirAnswer(set, { repHigh, isPrimary }) {
  return !!isPrimary && !set.isWarmup && Number(set.reps) >= repHigh && !Number.isFinite(set.rir);
}

export function progressSets(opts) {
  const { repLow, repHigh, increment, isPrimary, painFlag } = opts;
  let required = clampRequired(opts.required);
  let next = num(opts.startWeight);
  let jump = opts.carryFrom != null && next != null ? { from: num(opts.carryFrom), to: next } : null;
  let shaved = null;
  let topHits = 0;
  let last = null;

  const work = (opts.sets || []).filter((s) => !s.isWarmup && Number.isFinite(Number(s.reps)) && s.reps !== null);
  for (const s of work) {
    const w = num(s.weight);
    const r = Number(s.reps);
    if (w == null) continue;

    if (painFlag === 'sharp') {
      next = w; jump = null; shaved = null;
      last = { action: 'pain', message: 'Sharp pain: end the set here. Swap to a lift that feels fine (p.259).' };
      continue;
    }

    if (r >= repHigh) {
      if (!rirCounts({ isPrimary, rir: s.rir })) {
        next = w; jump = null; shaved = null;
        last = { action: 'no_rir', message: `${repHigh} at failure doesn't count yet. Same weight until you get ${repHigh} with a rep left (p.329).` };
        continue;
      }
      topHits += 1;
      if (topHits >= required) {
        next = w + increment;
        jump = { from: w, to: next };
        shaved = null; topHits = 0; required = 1;
        last = { action: 'bump', message: `Top-rep set with reps left. Add ${increment} lb: next set ${next} (p.327).` };
      } else {
        next = w;
        last = { action: 'top', message: `Top-rep set ${topHits} of ${required}. ${required - topHits} more at ${w} in this workout and the weight goes up (p.327).` };
      }
      continue;
    }

    if (r < repLow) {
      if (jump && w === jump.to) {
        next = w - SHAVE_LB;
        shaved = { from: jump.from, to: next };
        jump = null;
        last = { action: 'shave', message: `Under ${repLow} at the new weight. Take ${SHAVE_LB} off: next set ${next} (p.327).` };
      } else if (shaved && w === shaved.to) {
        next = shaved.from;
        required = clampRequired(required + 1);
        shaved = null; topHits = 0;
        last = { action: 'revert', message: `Still under. Back to ${next} until you get ${required} top-rep sets in one workout (p.327).` };
      } else {
        next = w;
        last = { action: 'under', message: `Under ${repLow} reps. Same weight next time. If this keeps happening, look at food and sleep (p.327).` };
      }
      continue;
    }

    // In the range.
    if ((jump && w === jump.to) || (shaved && w === shaved.to)) {
      jump = null; shaved = null;
      last = { action: 'locked', message: `Locked in ${w}. Stay here until your next top-rep set.` };
    } else {
      last = { action: 'hold', message: `In the range. Same weight, chase a ${repHigh} with a rep left.` };
    }
    next = w;
  }

  // A jump earned on the very last set carries into next time (shave still applies there).
  const carryFrom = jump && last?.action === 'bump' ? jump.from : null;
  return { nextWeight: next, required, carryFrom, last };
}

// The book's session target when no top-rep set happens: beat last time's total
// reps across the hard sets by at least one (p.345).
export function totalReps(sets = []) {
  return sets.filter((s) => !s.isWarmup && !s.is_warmup).reduce((n, s) => n + (Number(s.reps) || 0), 0);
}

// Two warm-up sets before the first exercise for each major muscle group:
// 6 reps at about 50% of the hard-set weight, then 4 reps at about 70%,
// with a minute's rest after each (p.330-331).
export function warmupSets(workWeight) {
  const w = num(workWeight);
  if (!w || w <= 0) return [];
  return [
    { weight: round5(w * 0.5), reps: 6, isWarmup: true },
    { weight: round5(w * 0.7), reps: 4, isWarmup: true },
  ];
}

export const WARMUP_REST_SEC = 60;

function round5(n) { return Math.max(5, Math.round(n / 5) * 5); }
function num(v) { if (v === null || v === undefined || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
function clampRequired(r) { const n = Number(r); return Number.isFinite(n) ? Math.min(MAX_REQUIRED, Math.max(1, Math.round(n))) : 1; }
